/**
 * What the console reads from the builder's settings (Custom, owner 2026-09-27): the ink on a
 * Custom colour and its readable twins, the colour the builder paints, the sidebar, recent colours,
 * and a choice's name. Pure — no DOM.
 */
import {
  ADMIN_CUSTOM_COLOR_VALUES,
  ADMIN_CUSTOM_META,
  ADMIN_CUSTOM_OWN_COLOR,
  ADMIN_CUSTOM_RECENT_MAX,
  ADMIN_CUSTOM_THEME,
  ADMIN_THEME_META,
  type AdminCustomTheme,
  type AdminThemeChoice,
} from './admin-themes'
import { isAdminCustomHex } from './admin-ui'
import { composite, contrastRatio, parseCssColour, rgbToHex, type Rgb } from './colour-space'

// ── Readable Custom colours (review I1, 2026-09-27 — the owner's ruling for the console) ─────────
//
// The customer builder puts white on any colour darker than a luminance of 0.4, and writes links
// and figures in the colour itself. In the console that left links in the admin's own #facc15 at
// 1.53:1, Yellow's at 1.91, and white on a sky-blue button at 2.66:1. The ruling: the chosen colour
// still FILLS buttons, badges and accents; written as text it reads a text-strength twin, the focus
// ring a 3:1 twin, and the ink on a fill is whichever of white and near-black reads better. The
// customer app is unchanged (the owner is asked about it separately).

/**
 * The two inks on a Custom fill. White, and a near-black dark enough that one of the two ALWAYS
 * reaches 4.5:1: they cross near a luminance of 0.18, where both give 4.50:1 — a lighter dark ink
 * (the customer app's oklch(0.145 0 0)) would leave a band of mid-tone colours at 4.35–4.49:1.
 * It is also Clarity's dark-mode ink. __tests__/admin-theme-custom-contrast.spec.ts sweeps the
 * whole colour space.
 */
export const CUSTOM_INK = { light: 'oklch(1 0 0)', dark: 'oklch(0.12 0 0)' } as const

const INK_RGB = { light: parseCssColour(CUSTOM_INK.light).rgb, dark: parseCssColour(CUSTOM_INK.dark).rgb }

/** 'dark' or 'light': the ink that reads better on `colour` (`#rrggbb`, `#rgb` or `oklch(…)`). */
export function inkOn(colour: string): 'dark' | 'light' {
  const fill = parseCssColour(colour).rgb
  return contrastRatio(fill, INK_RGB.dark) > contrastRatio(fill, INK_RGB.light) ? 'dark' : 'light'
}

/** 'dark' or 'light' ink for the admin's own colour; null for anything that is not six hex digits. */
export function customColourInk(hex: string): 'dark' | 'light' | null {
  return isAdminCustomHex(hex) ? inkOn(`#${hex}`) : null
}

/**
 * The surfaces Custom's text sits on: its page, its cards and its menus (Classic's tokens —
 * app/(admin)/admin-theme.css, Custom's light and dark blocks; the contrast spec reads the sheet
 * and fails if these drift from it).
 */
const CUSTOM_SURFACES = {
  light: ['oklch(1 0 0)'],
  dark: ['oklch(0.145 0 0)', 'oklch(0.205 0 0)'],
} as const

/** How strong a tint of the colour a pressed label sits on (the menu's `bg-primary/10`). */
const PRESSED_TINT = 0.1

/** A Custom colour's readable twins, as `#rrggbb`, and the ink on it. */
export interface CustomColourTwins {
  ink: 'dark' | 'light'
  /** The colour written as text: 4.5:1 on the page, the card, the menu and its own 10% tint. */
  textLight: string
  textDark: string
  /** The focus ring: 3:1 on the page and the card. */
  ringLight: string
  ringDark: string
}

const mix = (a: Rgb, b: Rgb, t: number): Rgb => a.map((c, i) => c * (1 - t) + b[i] * t) as unknown as Rgb
const rgbOfHex = (hex: string): Rgb => parseCssColour(hex).rgb

/**
 * `colour` mixed toward `target` (black for light mode, white for dark) — as little as possible —
 * until it reaches `goal` against every surface. Mixed in sRGB, as `color-mix(in srgb, …)` would;
 * the answer is rounded to #rrggbb and checked again, so what the page paints is what was measured.
 */
function strengthen(colour: Rgb, target: Rgb, surfaces: readonly Rgb[], goal: number): string {
  const passes = (rgb: Rgb) => surfaces.every((s) => contrastRatio(rgb, s) >= goal)
  if (passes(rgbOfHex(rgbToHex(colour)))) return rgbToHex(colour)
  let [lo, hi] = [0, 1]
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2
    if (passes(mix(colour, target, mid))) hi = mid
    else lo = mid
  }
  for (let t = hi; t <= 1; t = Math.min(1, t + 0.002)) {
    const hex = rgbToHex(mix(colour, target, t))
    if (passes(rgbOfHex(hex)) || t === 1) return hex
  }
  return rgbToHex(target)
}

/** The ink and the readable twins of a Custom colour (any colour the builder offers, or `#rrggbb`). */
export function customColourTwins(colour: string): CustomColourTwins {
  const fill = parseCssColour(colour).rgb
  const [black, white]: Rgb[] = [
    [0, 0, 0],
    [1, 1, 1],
  ]
  const surfaces = (mode: 'light' | 'dark') => CUSTOM_SURFACES[mode].map((s) => parseCssColour(s).rgb)
  const withTints = (mode: 'light' | 'dark') => surfaces(mode).flatMap((s) => [s, composite(fill, PRESSED_TINT, s)])
  return {
    ink: inkOn(colour),
    textLight: strengthen(fill, black, withTints('light'), 4.5),
    textDark: strengthen(fill, white, withTints('dark'), 4.5),
    ringLight: strengthen(fill, black, surfaces('light'), 3),
    ringDark: strengthen(fill, white, surfaces('dark'), 3),
  }
}

/**
 * The twins to put on <html> for a builder — the colour it paints — or null for Default, which is
 * Classic's own primary and reads as it is.
 */
export function customTwinsFor(custom: AdminCustomTheme): CustomColourTwins | null {
  if (custom.color === 'default') return null
  if (custom.color === ADMIN_CUSTOM_OWN_COLOR) return isAdminCustomHex(custom.hex) ? customColourTwins(`#${custom.hex.toLowerCase()}`) : null
  return customColourTwins(ADMIN_CUSTOM_COLOR_VALUES[custom.color])
}
/** The colour the builder paints: a grid colour's value, or the admin's own `#hex`. */
export function customColourValue(custom: AdminCustomTheme): string {
  if (custom.color === ADMIN_CUSTOM_OWN_COLOR) return isAdminCustomHex(custom.hex) ? `#${custom.hex.toLowerCase()}` : ADMIN_CUSTOM_COLOR_VALUES.default
  return ADMIN_CUSTOM_COLOR_VALUES[custom.color]
}

/**
 * The sidebar's variant for the theme on screen: a ready-made theme's own; for Custom, the
 * builder's — `default` is the docked sidebar, so an admin who never touched it keeps it docked.
 */
export function adminSidebarVariant(theme: AdminThemeChoice, custom?: AdminCustomTheme): 'sidebar' | 'floating' | 'inset' {
  if (theme !== ADMIN_CUSTOM_THEME) return ADMIN_THEME_META[theme].sidebar
  const sidebar = custom?.sidebar ?? 'default'
  return sidebar === 'default' ? 'sidebar' : sidebar
}

/** `hex` at the front of the recent colours, once, keeping at most ADMIN_CUSTOM_RECENT_MAX. */
export function withRecentColour(recent: readonly string[], hex: string): string[] {
  const colour = hex.toLowerCase()
  return [colour, ...recent.filter((c) => c !== colour)].slice(0, ADMIN_CUSTOM_RECENT_MAX)
}

/** A choice's name, as the picker shows it. */
export function adminThemeLabel(theme: AdminThemeChoice): string {
  return theme === ADMIN_CUSTOM_THEME ? ADMIN_CUSTOM_META.label : ADMIN_THEME_META[theme].label
}
